import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { container } from '@infrastructure/container';
import { CreateSupplierRequestDTO, UpdateSupplierRequestDTO, CreateSupplierAddressRequestDTO, LinkProductRequestDTO } from '@infrastructure/suppliers/dto/SupplierDTO';

export const useSupplier = (id: string) => {
  const queryClient = useQueryClient();

  const supplierQuery = useQuery({
    queryKey: ['supplier', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const result = await container.supplierRepository.getSupplier(id);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    }
  });

  const updateSupplierMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: UpdateSupplierRequestDTO }) => {
      const result = await container.supplierRepository.updateSupplier(id, updates);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { id }) => {
      // Invalidate and refetch specific supplier and suppliers list
      queryClient.invalidateQueries({ queryKey: ['supplier', id] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: async (supplierId: string) => {
      const result = await container.supplierRepository.deleteSupplier(supplierId);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: () => {
      // Invalidate and refetch suppliers list
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });

  const linkProductsMutation = useMutation({
    mutationFn: async ({ supplierId, products }: { supplierId: string; products: LinkProductRequestDTO[] }) => {
      const result = await container.supplierRepository.linkProducts(supplierId, products);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { supplierId }) => {
      // Invalidate and refetch specific supplier and suppliers list
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });

  const unlinkProductsMutation = useMutation({
    mutationFn: async ({ supplierId, productIds }: { supplierId: string; productIds: string[] }) => {
      const result = await container.supplierRepository.unlinkProducts(supplierId, productIds);
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { supplierId, productIds }) => {
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      for (const id of productIds) {
        queryClient.invalidateQueries({ queryKey: ['product', id] });
      }
    }
  });

  const addAddressMutation = useMutation({
    mutationFn: async ({ supplierId, address, currentSupplier }: {
      supplierId: string;
      address: CreateSupplierAddressRequestDTO;
      currentSupplier: any;
    }) => {
      // Create new address with a temporary ID
      const newAddress = {
        id: `temp-${Date.now()}`,
        ...address
      };

      // Add to existing addresses
      const updatedAddresses = [...currentSupplier.addresses, newAddress];

      const result = await container.supplierRepository.updateSupplier(supplierId, {
        addresses: updatedAddresses
      });
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { supplierId }) => {
      // Invalidate and refetch specific supplier and suppliers list
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });

  const removeAddressMutation = useMutation({
    mutationFn: async ({ supplierId, addressId, currentSupplier }: {
      supplierId: string;
      addressId: string;
      currentSupplier: any;
    }) => {
      // Remove address from existing addresses
      const updatedAddresses = currentSupplier.addresses.filter((addr: any) => addr.id !== addressId);

      const result = await container.supplierRepository.updateSupplier(supplierId, {
        addresses: updatedAddresses
      });
      if (result.status === 'error') {
        throw new Error(result.error.message);
      }
      return result.data;
    },
    onSuccess: (_, { supplierId }) => {
      // Invalidate and refetch specific supplier and suppliers list
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    }
  });

  return {
    supplier: supplierQuery.data || undefined,
    isLoading: supplierQuery.isLoading,
    error: supplierQuery.error,
    updateSupplier: updateSupplierMutation.mutateAsync,
    isUpdating: updateSupplierMutation.isLoading,
    deleteSupplier: deleteSupplierMutation.mutateAsync,
    isDeleting: deleteSupplierMutation.isLoading,
    linkProducts: linkProductsMutation.mutateAsync,
    isLinkingProduct: linkProductsMutation.isLoading,
    unlinkProduct: ({ supplierId, productId }: { supplierId: string; productId: string }) =>
      unlinkProductsMutation.mutateAsync({ supplierId, productIds: [productId] }),
    unlinkProducts: unlinkProductsMutation.mutateAsync,
    isUnlinkingProduct: unlinkProductsMutation.isLoading,
    addAddress: addAddressMutation.mutateAsync,
    isAddingAddress: addAddressMutation.isLoading,
    removeAddress: removeAddressMutation.mutateAsync,
    isRemovingAddress: removeAddressMutation.isLoading
  };
}; 